import { browser } from '$app/environment';
import * as Sentry from '@sentry/browser';

import settingsService from '$shared/services/settingsService';
import { AppException } from '$shared/exceptions/AppException';
import UIErrorMessage from '$shared/constants/UIErrorMessage';
import Label from '$provider/constants/Label';
import ServiceError from '$shared/models/ServiceError';
import { UnhandledException } from '$shared/exceptions/UnhandledException';
import { ErrorStatusNotLoggedToSentry } from '$shared/constants/ErrorStatus';
import packageInfo from '$package';

const LocalDevEnv = 'local-dev';
const SentryEnv: string = (import.meta.env.VITE_SENTRY_ENV as string) || LocalDevEnv;
const SentryDsn: string = import.meta.env.VITE_SENTRY_DSN as string;

const isLocalDevMode = SentryEnv === LocalDevEnv;

// Ignore the sentry log for this organization
const WhitelistedOrganization = ['qa-test-automation', 'Salesforce QA'];

class SentryLogger {
  private isInitialized = false;
  private reportedWarningErrors = new WeakSet<object>();

  init() {
    try {
      const handleSentryException = this.handleSentryException.bind(this);
      if (isLocalDevMode) {
        console.info('Sentry not initialised for dev mode');
        return;
      }
      Sentry.init({
        dsn: SentryDsn,
        environment: SentryEnv.trim(),
        attachStacktrace: true,
        debug: true,
        maxBreadcrumbs: 0,
        ignoreErrors: [
          /^Unauthorized$/,
          /^Action forbidden. Please contact your administrator$/,
          /^Failed to fetch.*$/,
          /^NetworkError when attempting to fetch resource.*$/,
          /^The new password must be different from the old one.$/,
          /^Forbidden$/,
          /^Not Found$/
        ],
        release: `${packageInfo.name}@${import.meta.env.VITE_VERIFIER_VERSION as string}`,
        beforeSend: function (event, hint) {
          // -------- Finger print the sentry exception ---------- //

          const fingerPrintMessage: string[] | null = handleSentryException(event, hint);
          // If finger print message is null instead of string || empty string
          // then need to return null, it doesn't capture the log. To avoid unknown exception
          if (fingerPrintMessage === null) {
            return null;
          }

          event.fingerprint = (fingerPrintMessage.length > 0 && fingerPrintMessage) || [
            '{{ default }}'
          ];

          // -------- Finger print the sentry exception ---------- //
          return event;
        }
      });
      this.isInitialized = true;
      this.handleUnHandledExceptions();
    } catch (error) {
      console.error('Sentry initialization failed', error);
    }
  }

  log(error: AppException): void {
    if (isLocalDevMode) {
      console.trace('sentry log:', error);
      return;
    }
    if (ErrorStatusNotLoggedToSentry.includes(error.status)) {
      return;
    }
    if (WhitelistedOrganization.includes(settingsService.organizationName)) {
      return;
    }
    if (error.sendSentryAsWarn) {
      this.warn(error.message, error.getSentryOptions());
      return;
    }

    let data: unknown;
    if (!(error instanceof AppException)) {
      error = new AppException(error.title || error.message || 'Unknown error', { error });
    }
    error.organizationName = settingsService.organizationName || '';

    if (isLocalDevMode || !this.isInitialized) {
      return console.trace(error, data);
    }
    error.passToSentry && Sentry.captureException(error, error.getSentryOptions());
  }

  /**
   * For API failures or errors caught by multiple layers.
   * Use warn for standalone warnings and locally handled errors.
   */
  warnOnce(message: string, data?: any): void {
    const error = data instanceof Error ? data : data?.error ?? data?.err;

    // ApiErrorWrapper owns reporting (and intentional suppression) of ServiceErrors.
    if (error instanceof ServiceError) return;

    if (error !== null && typeof error === 'object') {
      if (this.reportedWarningErrors.has(error)) return;
      this.reportedWarningErrors.add(error);
    }

    this.warn(message, data);
  }

  warn(message: string, data: any) {
    if (isLocalDevMode) {
      console.trace('sentry warn:', message);
      return;
    }
    if (!data) {
      data = {};
    }
    data.organizationName = settingsService.organizationName;
    if (isLocalDevMode || !this.isInitialized) {
      return console.warn(message, data);
    }
    Sentry.captureMessage(message, {
      extra: data
    });
  }

  handleSentryException(event: Sentry.Event, hint: Sentry.EventHint): string[] | null {
    const exception = hint.originalException;
    let exceptionMessage = '';

    // Sentry API Exception ------- ignore unauthourized, not found error--------------- //
    if (ErrorStatusNotLoggedToSentry.includes(exception?.status)) {
      return null;
    }
    if (WhitelistedOrganization.includes(settingsService.organizationName)) {
      return null;
    }
    if (typeof exception === 'string') {
      exceptionMessage = exception;
    } else if (exception?.message) {
      try {
        // Sentry API Exception ------- error message--------------- //
        const sentryException = JSON.parse(exception.message);

        // Sentry API Exception ------- title --------------- //
        if (sentryException.title || sentryException.message) {
          exceptionMessage = sentryException.title || sentryException.message;
        }
      } catch {
        exceptionMessage = exception.title || exception.message;
        if (!event.extra) {
          event.extra = {};
        }
        event.extra.exceptionMessage = exceptionMessage;
      }
    } else {
      exceptionMessage = exception?.title || UIErrorMessage.UnknowError;
    }
    this.updateSentryTitle(event, exceptionMessage);

    // Get the updated error value from the sentry event
    const error: Sentry.Exception | undefined = event?.exception?.values?.first?.();
    if (!error) return [exceptionMessage].filter(Boolean); // If the error not available.then we can return the exception message;

    // Return the sentry exception with title and message for grouping
    // it improves the grouping rules with comparing 2 values.
    return [error?.type, error?.value].filter(Boolean) as string[];
  }

  private updateSentryTitle(event: Sentry.Event, exceptionMessage: string): void {
    // Sentry Exception ------- reassign title  --------------- //
    const error = event?.exception?.values?.first?.();
    if (error?.type && exceptionMessage) {
      error.type = exceptionMessage;
    }
    if (error?.mechanism && !error?.mechanism?.handled) {
      error.value = exceptionMessage;
    }
    // If value has empty or 'No error message', then set error value
    if (error && (!error?.value || error?.value === Label.NoErrorMessage)) {
      error.value = UIErrorMessage.UnknowError;
    }

    // Setting the sentry title for info level logs.
    if (event?.level === 'info' && error) {
      error.type = `Warning: ${exceptionMessage}`;
    }
  }

  private handleUnHandledExceptions(): void {
    if (!browser) return;
    window.onunhandledrejection = (event: PromiseRejectionEvent) => {
      if (event.reason instanceof ServiceError) {
        const exception = UnhandledException.from('Unhandled Error', event.reason, event.reason);
        this.log(exception);
        return;
      }
      Sentry.captureException(event.reason);
    };
  }
}

const sentryLogger = new SentryLogger();
sentryLogger.init();

export const sentry = sentryLogger;
